import React from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";

import "./NewProducts.css";

/* ============================================================
   API
============================================================ */

const API =
  process.env.NODE_ENV === "production"
    ? "https://gj-frio-industrial-api.onrender.com"
    : "http://localhost:4000";

/* ============================================================
   OBTENER NUEVOS PRODUCTOS
============================================================ */

const obtenerNuevosProductos = async () => {
  const respuesta = await axios.get(`${API}/api/new-products`, {
    timeout: 45000,
  });

  const datos = Array.isArray(respuesta.data) ? respuesta.data : [];

  return [...datos].sort((a, b) => Number(a.orden || 0) - Number(b.orden || 0));
};

/* ============================================================
   COMPONENTE
============================================================ */

const NewProducts = () => {
  /* ==========================================================
     TANSTACK QUERY
  ========================================================== */

  const {
    data: productos = [],
    isError,
    error,
  } = useQuery({
    queryKey: ["new-products"],
    queryFn: obtenerNuevosProductos,

    // También están definidos globalmente en queryClient.js.
    staleTime: Infinity,
    gcTime: Infinity,
  });

  /* ==========================================================
     ERROR
  ========================================================== */

  if (isError) {
    console.error("Error cargando nuevos productos:", error);
  }

  /* ==========================================================
     SIN PRODUCTOS
  ========================================================== */

  if (productos.length === 0) {
    return null;
  }

  /* ==========================================================
     DIVIDIR PRODUCTOS DE 3 EN 3
  ========================================================== */

  const slides = [];

  for (let i = 0; i < productos.length; i += 3) {
    slides.push(productos.slice(i, i + 3));
  }

  /* ==========================================================
     VISTA
  ========================================================== */

  return (
    <section className="new-products-section">
      <div className="new-products-container">
        {/* =====================================================
            ENCABEZADO
        ===================================================== */}

        <div className="new-products-header">
          <div>
            <span className="new-products-kicker">NOVEDADES</span>

            <h2>Nuevos productos</h2>

            <p>
              Conoce algunos de nuestros productos para instalaciones a gas,
              conexiones y trabajos técnicos.
            </p>
          </div>

          <div className="new-products-header-line"></div>
        </div>

        {/* =====================================================
            CARRUSEL
        ===================================================== */}

        <div
          id="carouselExampleControls3"
          className="carousel slide new-products-carousel"
          data-bs-ride={slides.length > 1 ? "carousel" : undefined}
          data-bs-interval="6000"
        >
          <div className="carousel-inner">
            {slides.map((slide, slideIndex) => (
              <div
                key={slideIndex}
                className={`carousel-item ${slideIndex === 0 ? "active" : ""}`}
              >
                <div className="new-products-grid">
                  {slide.map((producto, index) => (
                    <article
                      className="new-product-card"
                      key={producto._id || `${slideIndex}-${index}`}
                    >
                      {/* =========================================
                          IMAGEN
                      ========================================= */}

                      <div className="new-product-image-container">
                        <img
                          src={producto.imagen}
                          alt={producto.nombre}
                          className="new-product-image"
                          loading="lazy"
                          decoding="async"
                          fetchpriority="low"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />

                        <span className="new-product-badge">Nuevo</span>

                        {producto.categoria && (
                          <span className="new-product-category">
                            {producto.categoria}
                          </span>
                        )}
                      </div>

                      {/* =========================================
                          INFORMACIÓN
                      ========================================= */}

                      <div className="new-product-body">
                        <h3>{producto.nombre}</h3>

                        {producto.descripcion && <p>{producto.descripcion}</p>}

                        <div className="new-product-footer">
                          <span>
                            <i className="bi bi-check-circle-fill"></i>
                            Disponible
                          </span>

                          <span className="new-product-brand">G&J Frío</span>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* ===================================================
              CONTROLES
          =================================================== */}

          {slides.length > 1 && (
            <>
              <button
                className="carousel-control-prev new-products-control new-products-control-prev"
                type="button"
                data-bs-target="#carouselExampleControls3"
                data-bs-slide="prev"
              >
                <span className="new-products-arrow">
                  <i className="bi bi-chevron-left"></i>
                </span>

                <span className="visually-hidden">Anterior</span>
              </button>

              <button
                className="carousel-control-next new-products-control new-products-control-next"
                type="button"
                data-bs-target="#carouselExampleControls3"
                data-bs-slide="next"
              >
                <span className="new-products-arrow">
                  <i className="bi bi-chevron-right"></i>
                </span>

                <span className="visually-hidden">Siguiente</span>
              </button>

              <div className="carousel-indicators new-products-indicators">
                {slides.map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    data-bs-target="#carouselExampleControls3"
                    data-bs-slide-to={index}
                    className={index === 0 ? "active" : ""}
                    aria-current={index === 0 ? "true" : undefined}
                    aria-label={`Grupo ${index + 1}`}
                  ></button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default NewProducts;
